import { Link, useLocation } from 'react-router-dom';
import { Calendar, CheckSquare, BarChart3 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export const navigationItems = [
  { href: '/app/meetings', label: 'Meetings', icon: Calendar },
  { href: '/app/actions', label: 'Actions', icon: CheckSquare },
  { href: '/app/analytics', label: 'Analytics', icon: BarChart3, requiresPremium: true },
];

export function Sidebar() {
  const location = useLocation();
  const { subscription } = useAuth();

  // Detect if we're on a meeting detail page (icon-only mode)
  const isMeetingDetailPage = location.pathname.match(/^\/app\/meetings\/[^/]+$/);
  const isIconOnly = !!isMeetingDetailPage;

  // Helper function to check if user has access to analytics
  const hasAnalyticsAccess = () => {
    if (!subscription) return false;
    
    // Allow paid subscribers
    if (subscription.status === 'paid') return true;
    
    // Allow trial users if not expired
    if (subscription.status === 'trial') {
      if (!subscription.trialExpiresOn) return true;
      return new Date() <= new Date(subscription.trialExpiresOn);
    }
    
    return false;
  };

  // Filter navigation items based on access
  const visibleItems = navigationItems.filter(item => {
    if (item.requiresPremium) {
      return hasAnalyticsAccess();
    }
    return true;
  });

  return (
    <aside className={cn(
      "pb-12 glass-sidebar transition-all duration-300 z-50",
      isIconOnly ? "w-20" : "w-64"
    )}>
      <div className={cn("space-y-4", isIconOnly ? "pt-4" : "pt-6")}>
        <div className={cn("py-2", isIconOnly ? "px-2" : "px-3")}>
          <div className="space-y-2">
            <TooltipProvider delayDuration={300}>
              {visibleItems.map((link) => (
                <Tooltip key={link.href}>
                  <TooltipTrigger asChild>
                    <Link
                      to={link.href}
                      className={cn(
                        "flex items-center rounded-xl py-2 text-sm transition-all duration-300 border",
                        isIconOnly ? "justify-center px-2" : "gap-3 px-3",
                        location.pathname === link.href || (link.href === '/app/meetings' && isMeetingDetailPage)
                          ? "bg-gradient-to-r from-indigo-500/10 to-violet-500/10 border-indigo-500/20 shadow-lg shadow-indigo-500/10 font-medium" 
                          : "border-transparent hover:bg-gradient-to-r hover:from-indigo-500/5 hover:to-violet-500/5 hover:border-indigo-500/20 hover:shadow-lg hover:shadow-indigo-500/10 hover:-translate-y-0.5"
                      )}
                    >
                      <link.icon className="h-5 w-5 flex-shrink-0" />
                      {!isIconOnly && <span>{link.label}</span>}
                    </Link>
                  </TooltipTrigger>
                  {isIconOnly && (
                    <TooltipContent side="right">
                      <p>{link.label}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              ))}
            </TooltipProvider>
          </div>
        </div>
      </div>
    </aside>
  );
}
